// We importeren de nodige React-hooks en hulpfuncties
import { useContext, useState, useMemo, useRef } from "react";
// useContext: om data uit de Context te halen (zoals CSV-data)
// useState: om lokale variabelen te beheren (zoals filter)
// useMemo: om berekeningen te optimaliseren
// useRef: om een referentie te maken naar de grafiek (voor export)

// We importeren de context waar onze CSV-data in wordt opgeslagen
import { CsvDataContext } from "./Context.jsx";

// We importeren de component voor de grafiek en het teamfilter
import BarChartVer from "./BarChartVer.jsx";
import Teams from "./Teams.jsx";

// We importeren libraries om exports te kunnen maken
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Dit is de hoofdcomponent voor de "Defensive Actions" grafiek
export default function DefensiveAction() {
  // We halen de CSV-data (rawData) en de laadstatus (loading) uit onze context
  const { rawData, loading } = useContext(CsvDataContext);

  // Teamfilter: hiermee kan de gebruiker filteren op een specifiek team
  const [teamFilter, setTeamFilter] = useState("");

  // chartRef gebruiken we later om de grafiek te exporteren als afbeelding of PDF
  const chartRef = useRef();

  // useMemo: we groeperen de ruwe CSV-data zodat het efficiënter is
  // Alleen als rawData verandert, wordt deze berekening opnieuw gedaan
  const chartData = useMemo(() => {
    const grouped = rawData.reduce((acc, row) => {
      // We halen de spelernaam, teamnaam en resultaat uit de CSV-rij
      const playerName = row["Player Name"];
      const teamName = row["Team Name"];
      const result = row["Result"]?.trim().toLowerCase();

      // Als er iets ontbreekt, slaan we deze rij over
      if (!playerName || !result || !teamName) return acc;

      // Als deze speler nog niet in de lijst staat, voegen we hem toe
      if (!acc[playerName]) {
        acc[playerName] = {
          player: playerName,
          team: teamName,
          defensive_action_unsuccesful: 0,
          defensive_action_succesful: 0,
          defensive_action_total: 0,
        };
      }

      // We tellen hoeveel succesvolle en niet-succesvolle "defensive actions" elke speler heeft
      if (row.Event?.trim().toLowerCase() === "defensive action") {
        if (result === "unsuccessful")
          acc[playerName].defensive_action_unsuccesful += 1;
        if (result === "successful")
          acc[playerName].defensive_action_succesful += 1;

        // We berekenen het totaal van beide waarden
        acc[playerName].defensive_action_total =
          acc[playerName].defensive_action_unsuccesful +
          acc[playerName].defensive_action_succesful;
      }
      return acc;
    }, {});

    // We zetten het resultaat om naar een array zodat het in de grafiek kan
    return Object.values(grouped);
  }, [rawData]);

  // Als een team is gekozen, filteren we de data daarop
  const filteredData = teamFilter
    ? chartData.filter((item) => item.team === teamFilter)
    : chartData;

  // 📸 Exportfunctie: grafiek exporteren als afbeelding (PNG)
  const exportAsImage = async () => {
    // We maken een afbeelding van het element dat we via chartRef refereren
    const canvas = await html2canvas(chartRef.current);
    // We maken een downloadlink aan
    const link = document.createElement("a");
    link.download = "defensive_actions.png"; // bestandsnaam
    link.href = canvas.toDataURL("image/png"); // zet canvas om naar PNG
    link.click(); // start de download
  };

  // 🧾 Exportfunctie: grafiek exporteren als PDF
  const exportAsPDF = async () => {
    const canvas = await html2canvas(chartRef.current); // maak screenshot van de grafiek
    const imgData = canvas.toDataURL("image/png"); // zet de afbeelding om naar PNG-formaat
    const pdf = new jsPDF("landscape"); // maak een PDF in liggend formaat
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, "PNG", 10, 10, width - 20, height - 20); // voeg de afbeelding toe aan de PDF
    pdf.save("defensive_actions.pdf"); // download de PDF
  };

  // 💾 Exportfunctie: data exporteren als CSV-bestand
  const exportAsCSV = () => {
    const csvRows = [];
    // We halen de kolomnamen uit het eerste object (headers)
    const headers = Object.keys(chartData[0] || {});
    csvRows.push(headers.join(","));

    // We voegen alle rijen toe aan het CSV-bestand
    for (const row of chartData) {
      const values = headers.map((h) => row[h]);
      csvRows.push(values.join(","));
    }

    // We maken een Blob aan (bestand in het geheugen)
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.download = "defensive_actions_data.csv"; // bestandsnaam
    link.href = URL.createObjectURL(blob); // maak een downloadlink
    link.click(); // start de download
  };

  // Als de data nog wordt geladen, tonen we een melding
  if (loading) return <p>Data wordt geladen...</p>;

  // 🔹 Hier begint de HTML-structuur die wordt weergegeven op het scherm
  return (
    <>
      {/* Bovenste gedeelte met titel en exportknoppen */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Defensive Actions</h2>

        {/* Exportknoppen */}
        <div className="flex gap-2">
          <button
            onClick={exportAsImage}
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
          >
            📊 PNG
          </button>
          <button
            onClick={exportAsPDF}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
          >
            🧾 PDF
          </button>
          <button
            onClick={exportAsCSV}
            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
          >
            💾 CSV
          </button>
        </div>
      </div>

      {/* Filtercomponent om per team te filteren */}
      <Teams
        chartData={chartData}
        teamFilter={teamFilter}
        setTeamFilter={setTeamFilter}
      />

      {/* De grafiek zelf */}
      <div ref={chartRef} className="bg-white p-4 rounded-2xl shadow-md">
        <BarChartVer
          data={filteredData} // gefilterde data per team
          yAxisKey="team" // wat op de y-as komt
          xAxisKey="player" // wat op de x-as komt
          bars={[
            {
              dataKey: "defensive_action_unsuccesful",
              fill: "#8884d8",
              name: "Unsuccesful",
            },
            {
              dataKey: "defensive_action_succesful",
              fill: "#82ca9d",
              name: "Succesful",
            },
            {
              dataKey: "defensive_action_total",
              fill: "#ffc658",
              name: "Total Actions",
            },
          ]}
        />
      </div>
    </>
  );
}
